import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { offerAPI } from "../utils/api";
import toast from "react-hot-toast";
import "./AdminOfferPage.css";

export default function AdminOfferPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [offer, setOffer] = useState(null);
  const [offerText, setOfferText] = useState("");
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    const userRole = localStorage.getItem("user_role");
    const userData = localStorage.getItem("user");

    // Admin guard
    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch {}
    }

    fetchOfferDetails(token);
  }, [navigate]);

  const fetchOfferDetails = async (token) => {
    try {
      setLoading(true);
      const data = await offerAPI.getOfferDetails();
      
      if (data.success) {
        setOffer(data.offer);
        setOfferText(data.offer?.offer_text || "");
      }
    } catch (error) {
      console.error("Error fetching offer details:", error);
      toast.error("Failed to load offer details");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOffer = async () => {
    if (!offerText.trim()) {
      toast.error("Please enter an offer text");
      return;
    }

    if (offerText.length > 1000) {
      toast.error("Offer text must be 1000 characters or less");
      return;
    }

    try {
      setSaving(true);
      const data = await offerAPI.saveOffer(offerText.trim());
      
      if (data.success) {
        setOffer(data.offer);
        toast.success(data.message);
      }
    } catch (error) {
      console.error("Error saving offer:", error);
      toast.error(error.message || "Failed to save offer");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOffer = async () => {
    if (!offer) {
      toast.error("No offer to delete");
      return;
    }

    if (!window.confirm("Are you sure you want to delete the current offer?")) {
      return;
    }

    try {
      setDeleting(true);
      const data = await offerAPI.deleteOffer();
      
      if (data.success) {
        setOffer(null);
        setOfferText("");
        toast.success(data.message);
      }
    } catch (error) {
      console.error("Error deleting offer:", error);
      toast.error(error.message || "Failed to delete offer");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-offer-page">
          <div className="loading">Loading offer details...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-offer-page">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Offer Management</h1>
            <p>Manage the promotional offer text displayed to users</p>
          </div>
          <div className="admin-actions">
            <button className="btn-secondary" onClick={() => navigate("/")}>
              View Store
            </button>
          </div>
        </div>

        {/* Current Offer Status */}
        <div className="offer-status-card">
          <div className="status-header">
            <h3>Current Offer Status</h3>
            <div className={`status-badge ${offer ? 'active' : 'inactive'}`}>
              {offer ? 'Active' : 'No Offer'}
            </div>
          </div>
          
          {offer && (
            <div className="current-offer">
              <h4>Current Offer Text:</h4>
              <div className="offer-preview">
                "{offer.offer_text}"
              </div>
              <div className="offer-meta">
                <span>Created: {new Date(offer.created_at).toLocaleDateString()}</span>
                <span>Updated: {new Date(offer.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          )}
        </div>

        {/* Offer Management Form */}
        <div className="offer-form-card">
          <h3>{offer ? 'Update Offer' : 'Create New Offer'}</h3>
          
          <div className="form-group">
            <label htmlFor="offer-text">Offer Text</label>
            <textarea
              id="offer-text"
              value={offerText}
              onChange={(e) => setOfferText(e.target.value)}
              placeholder="Enter your promotional offer text here..."
              maxLength={1000}
              rows={4}
            />
            <div className="char-count">
              {offerText.length}/1000 characters
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={handleSaveOffer}
              disabled={saving || !offerText.trim()}
            >
              {saving ? 'Saving...' : (offer ? 'Update Offer' : 'Create Offer')}
            </button>
            
            {offer && (
              <button
                type="button"
                className="btn-danger"
                onClick={handleDeleteOffer}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Offer'}
              </button>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="help-card">
          <h3>How it works</h3>
          <ul>
            <li>Only one offer can be active at a time</li>
            <li>Creating a new offer will update the existing one</li>
            <li>The offer text will be displayed to all users on the homepage</li>
            <li>Offer text is limited to 1000 characters</li>
            <li>Users can see the offer without logging in</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}
